export type Locale = 'en' | 'ar';

export const dict = {
  nav: {
    features: { en: 'Features', ar: 'المزايا' },
    howItWorks: { en: 'How it works', ar: 'طريقة العمل' },
    retailers: { en: 'Retailers', ar: 'المتاجر' },
    faq: { en: 'FAQ', ar: 'الأسئلة' },
    install: { en: 'Install', ar: 'ثبّت الآن' },
  },
  hero: {
    eyebrow: {
      en: 'For the luxury shopper in the GCC',
      ar: 'لمتسوّق الفخامة في الخليج',
    },
    title1: { en: 'Know the', ar: 'اعرف' },
    title2: { en: 'real price', ar: 'السعر الحقيقي' },
    title3: { en: 'of luxury.', ar: 'لقطع الفخامة.' },
    subtitle: {
      en: 'Sophia compares designer pieces across 42+ luxury retailers and estimates resale value — so you buy smarter in Dubai, Riyadh, and beyond.',
      ar: 'صوفيا تقارن قطع المصممين عبر أكثر من 42 متجر فخامة وتقدّر قيمة إعادة البيع — لتشتري بذكاء في دبي والرياض وما بعد.',
    },
    ctaPrimary: { en: 'Add to Chrome — Free', ar: 'أضف إلى كروم — مجانًا' },
    ctaSecondary: { en: 'See how it works', ar: 'شاهد طريقة العمل' },
    trust: {
      en: 'Works on Farfetch · Net-a-Porter · Mytheresa · The RealReal · Ounass · +37 more',
      ar: 'يعمل على فارفتش · نت آ بورتيه · ماي تيريزا · ذا ريل ريل · أُناس · و37 موقعًا آخر',
    },
  },
  trust: {
    badge: { en: 'PRIVACY FIRST', ar: 'الخصوصية أولًا' },
    title: {
      en: 'We never read your page HTML, email, or browsing history.',
      ar: 'لا نقرأ HTML صفحتك ولا بريدك ولا سجل تصفحك.',
    },
    items: [
      {
        en: 'Only product title and price',
        ar: 'اسم المنتج والسعر فقط',
      },
      {
        en: 'Explicit consent on first run',
        ar: 'موافقة صريحة عند التشغيل',
      },
      {
        en: 'PDPL & GDPR compliant',
        ar: 'متوافق مع PDPL و GDPR',
      },
    ],
  },
  features: {
    eyebrow: { en: 'The Sophia difference', ar: 'فرق صوفيا' },
    heading: {
      en: 'Built for the luxury buyer, not the mass market.',
      ar: 'مصمّم لمشتري الفخامة، لا للسوق العام.',
    },
    items: [
      {
        emoji: '💎',
        title: { en: 'Price across 42+ houses', ar: 'السعر في أكثر من 42 متجرًا' },
        body: {
          en: 'One click compares your bag on Farfetch, Net-a-Porter, The RealReal, Vestiaire, and GCC boutiques like The Luxury Closet.',
          ar: 'نقرة واحدة تقارن سعر حقيبتك في فارفتش ونت آ بورتيه وذا ريل ريل وفيستيير ومتاجر الخليج كذا لاكجري كلوزت.',
        },
      },
      {
        emoji: '📈',
        title: { en: 'Resale as investment', ar: 'إعادة البيع كاستثمار' },
        body: {
          en: 'Every piece gets a resale estimate with confidence score. Your Birkin, Rolex, or Patek is an asset — know its worth.',
          ar: 'كل قطعة تحصل على تقدير لإعادة البيع مع درجة ثقة. بِركن أو رولكس أو باتيك — اعرف قيمتها.',
        },
      },
      {
        emoji: '🌙',
        title: { en: 'GCC-first, bilingual', ar: 'الخليج أولًا، بلغتين' },
        body: {
          en: 'AED & SAR pricing, full Arabic with RTL, and 27+ regional boutiques built in — nobody else covers this market.',
          ar: 'تسعير بالدرهم والريال، عربية كاملة RTL، و27+ بوتيكًا إقليميًا مدمجًا — لا يغطي هذا السوق غيرنا.',
        },
      },
    ],
  },
  resale: {
    eyebrow: { en: 'Resale value', ar: 'قيمة إعادة البيع' },
    heading: {
      en: 'Your closet is a portfolio.',
      ar: 'خزانتك محفظة استثمارية.',
    },
    body: {
      en: 'A Hermès Birkin holds 80–100% of retail. A Rolex Submariner often appreciates. Sophia shows resale retention for every piece you view, so you can tell a trophy from a depreciating asset.',
      ar: 'حقيبة هيرميس بِركن تحافظ على 80–100% من السعر. رولكس سَبمارينر تزيد قيمته غالبًا. صوفيا تعرض نسبة إعادة البيع لكل قطعة، لتميّز القطعة الاستثمارية من القطعة المنخفضة القيمة.',
    },
    exampleLabel: { en: 'Live examples', ar: 'أمثلة حيّة' },
    examples: [
      { brand: 'Hermès', model: 'Birkin 25', retention: 95 },
      { brand: 'Rolex', model: 'Submariner', retention: 110 },
      { brand: 'Chanel', model: 'Classic Flap', retention: 88 },
      { brand: 'Louis Vuitton', model: 'Neverfull', retention: 72 },
      { brand: 'Gucci', model: 'GG Marmont', retention: 45 },
    ],
  },
  how: {
    eyebrow: { en: 'In 3 seconds', ar: 'خلال 3 ثوانٍ' },
    heading: { en: 'It just works.', ar: 'يعمل ببساطة.' },
    steps: [
      {
        n: 1,
        title: { en: 'Browse normally', ar: 'تصفّح كالعادة' },
        body: {
          en: 'Shop Farfetch, Ounass, any of 42 sites. Sophia sleeps until you land on a product page.',
          ar: 'تسوّق في فارفتش أو أُناس أو أي من الـ42 موقعًا. صوفيا لا تعمل حتى تصل لصفحة منتج.',
        },
      },
      {
        n: 2,
        title: { en: 'Tap the button', ar: 'انقر الزر' },
        body: {
          en: 'A soft "Should I Buy This?" pill appears bottom-right. One tap fetches the comparison.',
          ar: 'يظهر زر هادئ "هل أشتري؟" أسفل يمين. نقرة واحدة تجلب المقارنة.',
        },
      },
      {
        n: 3,
        title: { en: 'Decide with data', ar: 'قرّر ببيانات' },
        body: {
          en: 'See price across retailers, savings, and resale estimate in one panel. Close it, buy elsewhere, or pass.',
          ar: 'شاهد السعر عند المتاجر والتوفير وتقدير إعادة البيع في لوحة واحدة. أغلق، أو اشترِ، أو تجاوز.',
        },
      },
    ],
  },
  retailers: {
    eyebrow: { en: '42+ retailers', ar: 'أكثر من 42 متجرًا' },
    heading: { en: 'The places you actually shop.', ar: 'الأماكن التي تتسوق فيها فعلًا.' },
    groups: {
      international: { en: 'International Luxury', ar: 'فخامة عالمية' },
      resale: { en: 'Resale & Pre-owned', ar: 'إعادة بيع' },
      gcc: { en: 'GCC & Regional', ar: 'الخليج والمنطقة' },
    },
  },
  faq: {
    eyebrow: { en: 'FAQ', ar: 'الأسئلة' },
    heading: { en: 'Straight answers.', ar: 'إجابات صريحة.' },
    items: [
      {
        q: { en: 'Is it really free?', ar: 'هل هي مجانية فعلًا؟' },
        a: {
          en: 'Yes. Sophia earns a small affiliate fee when you buy through a compared link — you pay the same price, and we only suggest items where Sophia genuinely believes the deal is better.',
          ar: 'نعم. نكسب عمولة صغيرة إذا اشتريت عبر رابط المقارنة — لا تدفع أكثر، ونقترح فقط العروض التي نراها فعلًا أفضل.',
        },
      },
      {
        q: { en: 'Do you see my browsing history?', ar: 'هل تروْن سجل تصفّحي؟' },
        a: {
          en: 'No. Sophia activates only on 42 approved luxury domains and reads exactly two values from the page: product title and price. Nothing else leaves your browser.',
          ar: 'لا. صوفيا تعمل فقط على 42 نطاقًا فخامة معتمدًا وتقرأ قيمتين فقط: اسم المنتج والسعر. لا شيء آخر يغادر متصفّحك.',
        },
      },
      {
        q: { en: 'Does it support Arabic?', ar: 'هل تدعم العربية؟' },
        a: {
          en: 'Fully. Every string, RTL layout, Arabic-first typography. The extension auto-detects your browser language and can be toggled anytime.',
          ar: 'بالكامل. كل نصّ، تخطيط RTL، وخط عربي مقدَّم. الإضافة تكتشف لغة المتصفّح تلقائيًا ويمكن التبديل في أي وقت.',
        },
      },
      {
        q: { en: 'Which countries?', ar: 'أي دول؟' },
        a: {
          en: 'Built for the GCC — UAE, Saudi Arabia, Qatar, Bahrain, Kuwait, Oman — with AED/SAR pricing. Works globally though; the extension respects whatever locale your browser reports.',
          ar: 'مُصمّم للخليج — الإمارات والسعودية وقطر والبحرين والكويت وعُمان — بتسعير بالدرهم والريال. يعمل عالميًا أيضًا حسب لغة المتصفّح.',
        },
      },
      {
        q: { en: 'How is this different from Honey?', ar: 'ما الفرق عن Honey؟' },
        a: {
          en: 'Honey is built for mass-market coupons. Sophia is built for luxury: resale value, GCC retailers, Arabic, and a catalog that actually knows the difference between a Birkin and a Neverfull.',
          ar: 'Honey للخصومات العامة. صوفيا للفخامة: قيمة إعادة البيع، ومتاجر الخليج، والعربية، وكتالوج يميّز بِركن عن Neverfull.',
        },
      },
    ],
  },
  trending: {
    eyebrow: { en: 'Market intelligence', ar: 'ذكاء السوق' },
    heading: { en: 'Know what holds value.', ar: 'اعرف ما يحافظ على قيمته.' },
    subheading: {
      en: 'Annual resale value trends for the most-tracked luxury brands on Sophia.',
      ar: 'اتجاهات قيمة إعادة البيع السنوية لأكثر العلامات التجارية الفاخرة متابعة على صوفيا.',
    },
  },
  calculator: {
    eyebrow: { en: 'Investment calculator', ar: 'حاسبة الاستثمار' },
    heading: { en: 'Is your bag an investment?', ar: 'هل حقيبتك استثمار؟' },
    disclaimer: {
      en: 'Based on historical resale data. Past performance does not guarantee future results.',
      ar: 'بناءً على بيانات إعادة البيع التاريخية. الأداء السابق لا يضمن النتائج المستقبلية.',
    },
    brandLabel: { en: 'Select brand', ar: 'اختر العلامة التجارية' },
    priceLabel: { en: 'Purchase price', ar: 'سعر الشراء' },
    annualRate: { en: 'Average annual appreciation', ar: 'متوسط الارتفاع السنوي' },
    year: { en: 'year', ar: 'سنة' },
    years: { en: 'years', ar: 'سنوات' },
    projectionLabel: { en: 'Projected value', ar: 'القيمة المتوقعة' },
    vsToday: { en: 'vs today', ar: 'مقارنة باليوم' },
    footnote: {
      en: 'Estimates based on average resale data from Vestiaire Collective, Fashionphile, and Chrono24. Actual values depend on condition, color, and market demand.',
      ar: 'تقديرات مبنية على متوسط بيانات إعادة البيع من Vestiaire Collective و Fashionphile و Chrono24. القيم الفعلية تعتمد على الحالة واللون والطلب في السوق.',
    },
  },
  socialProof: {
    earlyAccess: { en: 'Early access feedback', ar: 'ملاحظات الوصول المبكر' },
    stats: {
      retailers: { en: 'Luxury retailers', ar: 'متجر فخامة' },
      gccSites: { en: 'GCC boutiques', ar: 'بوتيك خليجي' },
      authenticReferences: {
        en: 'Authenticated reference images',
        ar: 'صورة مرجعية مُصدّقة',
      },
      ensembleModels: { en: 'AI vision models per scan', ar: 'نموذج رؤية لكل فحص' },
      free: { en: 'Free forever', ar: 'مجانية للأبد' },
      dataTracking: { en: 'Personal data tracked', ar: 'بيانات شخصية محفوظة' },
    },
    quotes: [
      {
        text: {
          en: 'I saved AED 3,200 on a Chanel Classic Flap. Sophia found it cheaper on Vestiaire in seconds.',
          ar: 'وفّرت 3,200 درهم على حقيبة شانيل كلاسيك فلاب. صوفيا وجدتها أرخص على Vestiaire في ثوانٍ.',
        },
        name: 'Fatima A.',
        initials: 'FA',
        role: { en: 'Dubai, UAE', ar: 'دبي، الإمارات' },
      },
      {
        text: {
          en: 'Finally an extension that actually works on Ounass and The Luxury Closet. Nothing else covers GCC sites.',
          ar: 'أخيرًا إضافة تعمل فعلًا على أُناس وذا لاكجري كلوزت. لا شيء آخر يغطي مواقع الخليج.',
        },
        name: 'Sara M.',
        initials: 'SM',
        role: { en: 'Riyadh, KSA', ar: 'الرياض، السعودية' },
      },
    ],
  },
  newsletter: {
    eyebrow: { en: 'Stay in the loop', ar: 'ابقَ على اطلاع' },
    heading: { en: 'Price drop alerts coming soon.', ar: 'تنبيهات انخفاض الأسعار قريبًا.' },
    body: {
      en: 'Be the first to know when we launch wishlist tracking and automatic price drop notifications.',
      ar: 'كن أول من يعرف عند إطلاق تتبع قائمة الأمنيات وتنبيهات انخفاض الأسعار التلقائية.',
    },
    placeholder: { en: 'your@email.com', ar: 'your@email.com' },
    button: { en: 'Notify me', ar: 'أبلغني' },
    success: {
      en: 'You\'re on the list! We\'ll notify you when price alerts launch.',
      ar: 'أنت في القائمة! سنبلغك عند إطلاق تنبيهات الأسعار.',
    },
    error: { en: 'Please enter a valid email address.', ar: 'يرجى إدخال بريد إلكتروني صالح.' },
  },
  cta: {
    heading: {
      en: 'Every luxury purchase deserves a second opinion.',
      ar: 'كل مشتراة فاخرة تستحق رأيًا ثانيًا.',
    },
    button: { en: 'Add to Chrome — Free', ar: 'أضف إلى كروم — مجانًا' },
  },
  footer: {
    tagline: {
      en: 'Sophia — Luxury shopping intelligence for the GCC.',
      ar: 'صوفيا — ذكاء التسوّق الفاخر للخليج.',
    },
    made: { en: 'Made in Abu Dhabi · Built for Dubai & Riyadh', ar: 'صُنع في أبوظبي · لدبي والرياض' },
    privacy: { en: 'Privacy', ar: 'الخصوصية' },
    terms: { en: 'Terms', ar: 'الشروط' },
    contact: { en: 'Contact', ar: 'تواصل' },
  },
} as const;

export function t<T extends Record<Locale, string>>(node: T, locale: Locale): string {
  return node[locale];
}
